/**
 * SoleadLab 2.0 - Indication Popup
 *
 * Popup form for adding or editing indications.
 * Matches original editor (23).html form fields.
 *
 * The "Analyze Thermal" button lives in the header and toggles
 * the right panel between the indication form and thermal controls.
 *
 * REFERENCE: editor (23).html - openAddFormPopupAt / openEditFormPopup
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import { useProjectStore, useMapStore, useUIStore, INDICATION_TYPES, STATUS_LABELS, STATUS_ORDER } from '../../stores';
import type { IndicationType } from '../../stores';
import { useIsReadOnlyViewer } from '../../hooks/useShareViewer';
import { resolveImageSrc } from '../../utils';
import { resolveSignedUrls } from '../../services/signedAssetUrl';
import { buildTileUrl } from '../../services/tileApi';
import { getPhotoBlobEntry, uniqueRelPhotoPath, storePhotoBlob } from '../../utils/photoBlobs';
import { Button } from '../common';
import ThermalAnnotator from './thermal/ThermalAnnotator';
import ThermalControls from './thermal/ThermalControls';
import ThermalStats from './thermal/ThermalStats';
import ThermalAdvancedParams from './thermal/ThermalAdvancedParams';
import { useThermalAnnotator } from './thermal/useThermalAnnotator';
import type { ThermalAnalysisData } from './thermal';
import TopologySuggestionBanner from '../layout/TopologySuggestionBanner';
import { captureDay } from '../../utils/captureDate';
import { deriveObservations, isLatestObservation } from '../../utils/observations';
import { indCenter } from '../../utils/geometry';
import { plannedIndicationGeometry } from '../../utils/createIndication';
import './IndicationPopup.css';

interface IndicationPopupProps {
  /** Mode: 'add' for new indication, 'edit' for existing */
  mode: 'add' | 'edit';
  /** Coordinates for the indication [lng, lat] */
  coordinates: [number, number];
  /** Indication ID (required for edit mode) */
  indicationId?: string;
  /** Called when user saves the form */
  onSave: (data: IndicationFormData) => void;
  /** Called when user cancels */
  onCancel: () => void;
  /** Called when user deletes (edit mode only) */
  onDelete?: () => void;
}

export interface IndicationFormData {
  type: string;
  /** User-provided display name — only used when type === 'Custom'. */
  customTypeName?: string;
  station?: string;
  inverter?: string;
  mppt?: string;
  dccb?: string;
  string?: string;
  module?: string;
  captureDate?: string;
  linkLibrary?: string;
  urlOverride?: string;
  srtUrl?: string;
  notes?: string;
  createIssue?: boolean;
  thermalData?: ThermalAnalysisData;
  /** Relative path for uploaded image (e.g. "photos/IMG_001.jpg") */
  image?: string;
  /** Custom topology fields from label schema (e.g., "PV", "Block") */
  customFields?: Record<string, string>;
}

export default function IndicationPopup({
  mode,
  coordinates,
  indicationId,
  onSave,
  onCancel,
  onDelete,
}: IndicationPopupProps) {
  const { data, availableDates, selectedDate, visibleDates } = useProjectStore();
  const isReadOnly = useIsReadOnlyViewer();
  const setIndicationStatus = useProjectStore((s) => s.setIndicationStatus);
  const indicationViewMode = useMapStore((s) => s.indicationViewMode);

  // Find existing indication if in edit mode
  const existingIndication = mode === 'edit' && indicationId
    ? data.indications.find((i) => i.id === indicationId)
    : null;

  // ── Cross-inspection: which observation to DISPLAY (Build B / #5) ──
  // Current → the latest observation (current state). As-found → the observation
  // for the inspection being viewed (latest VISIBLE observed day), so filtering to
  // an earlier year shows THAT year's photo/thermal, not the newest. Toggling
  // As-found ↔ Current thus flips the popup photo between "as found then" and "now".
  const activeObs = useMemo(() => {
    if (!existingIndication) return null;
    const obs = deriveObservations(existingIndication); // oldest → newest
    if (obs.length === 0) return null;
    if (indicationViewMode === 'current') return obs[obs.length - 1];
    const visible = new Set(visibleDates);
    const inView = obs.filter((o) => o.date && visible.has(captureDay(o.date)));
    return inView.length ? inView[inView.length - 1] : obs[obs.length - 1];
  }, [existingIndication, indicationViewMode, visibleDates]);

  // Whether the displayed observation is the LATEST. When viewing a historical
  // observation, saving must NOT persist its thermal over the fault's current
  // one (the annotator loaded the old ROI just for display), so handleSave
  // preserves the existing thermal in that case.
  const isViewingLatestObs = useMemo(() => {
    if (!existingIndication) return true;
    return isLatestObservation(existingIndication, activeObs);
  }, [existingIndication, activeObs]);

  // Resolution status (Phase 1) derived view — absent status ⇒ 'open'.
  const currentStatus = existingIndication?.properties.status ?? 'open';
  const statusEvents = existingIndication?.properties.statusEvents ?? [];
  const latestStatusEvent = statusEvents.length ? statusEvents[statusEvents.length - 1] : null;

  // The rect a Save WILL create at this click — the banner derives on it so
  // the popup and quick-add run ONE engine (memoized: the derive keys on
  // geometry reference identity).
  const bannerStringModules = useUIStore((s) => s.defaultStringModules);
  const bannerOrientation = useUIStore((s) => s.defaultIndicationOrientation);

  const [formData, setFormData] = useState<IndicationFormData>(() => {
    return initialFormData();
  });

  const bannerLayouts = useProjectStore((s) => s.data.siteLayouts);
  const bannerLayoutAlias = useProjectStore((s) => s.data.siteLayout);
  const addGeometry = useMemo<GeoJSON.Geometry | null>(() => {
    if (mode === 'edit' || !coordinates) return null;
    // ONE shape source with buildManualIndication (T-14: cell-fitted size +
    // bearing; defaults off-grid) — the banner must derive on the exact
    // rect a Save will create.
    const layouts = bannerLayouts ?? (bannerLayoutAlias ? [bannerLayoutAlias] : []);
    return plannedIndicationGeometry(coordinates, formData.type as IndicationType, {
      layouts,
      defaultStringModules: bannerStringModules,
      defaultIndicationOrientation: bannerOrientation,
    }).geometry;
  }, [mode, coordinates?.[0], coordinates?.[1], formData.type, bannerStringModules, bannerOrientation, bannerLayouts, bannerLayoutAlias]);

  function initialFormData(): IndicationFormData {
    if (existingIndication) {
      return {
        type: existingIndication.type,
        customTypeName: existingIndication.properties.customTypeName || '',
        station: existingIndication.properties.station || '',
        inverter: existingIndication.properties.inverter || '',
        mppt: existingIndication.properties.mppt || '',
        dccb: existingIndication.properties.dccb || '',
        string: existingIndication.properties.string || '',
        module: existingIndication.properties.module || '',
        captureDate: captureDay(existingIndication.properties.captureDate) || selectedDate || availableDates[0] || new Date().toISOString().split('T')[0],
        linkLibrary: existingIndication.properties.linkLibrary || '',
        urlOverride: existingIndication.properties.urlOverride || '',
        srtUrl: existingIndication.properties.srtUrl || '',
        notes: existingIndication.properties.description || '',
        createIssue: existingIndication.properties.createIssue || false,
        customFields: existingIndication.properties.customFields || undefined,
      };
    }
    return {
      type: 'Hot spots',
      station: '',
      inverter: '',
      mppt: '',
      dccb: '',
      string: '',
      module: '',
      captureDate: selectedDate || availableDates[0] || new Date().toISOString().split('T')[0],
      linkLibrary: '',
      urlOverride: '',
      srtUrl: '',
      notes: '',
      createIssue: false,
    };
  }

  const handleChange = (field: keyof IndicationFormData, value: string | boolean | Record<string, string>) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Suggest previously-used custom names (for the Custom type datalist).
  const existingCustomNames = useMemo(() => {
    const names = new Set<string>();
    for (const ind of data.indications) {
      const n = ind.properties.customTypeName?.trim();
      if (ind.type === 'Custom' && n) names.add(n);
    }
    return Array.from(names).sort();
  }, [data.indications]);

  // ---- Thermal state ----
  const [thermalActive, setThermalActive] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const thermalDataRef = useRef<ThermalAnalysisData | undefined>(
    existingIndication?.properties.thermalData
  );
  const handleThermalDataChange = useCallback((td: ThermalAnalysisData) => {
    thermalDataRef.current = td;
  }, []);
  const annotator = useThermalAnnotator(handleThermalDataChange);

  // Load the ACTIVE observation's thermal data (year-aware); reloads when the
  // active observation changes (As-found/Current toggle or date filter) so the
  // ROI overlay always matches the photo being shown.
  useEffect(() => {
    if (activeObs?.thermalData) {
      annotator.load(activeObs.thermalData);
    } else {
      // No analysis on the viewed observation → clear, don't carry the other
      // year's ROI over (load(undefined) is a no-op, so clear explicitly).
      annotator.clearRois();
      annotator.clearPoints();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeObs?.thermalData]);

  // ---- Photo ----
  // A child fault (PFC-4B Model B) carries no image of its own — resolve its
  // SOURCE photo's image for display + thermal analysis ONLY. This is read-only:
  // it is never written back to the child (the upload state below stays bound to
  // the child's own image, so Save can't copy the source path into the child).
  const ownImagePath = existingIndication?.properties.image || '';
  const ownImageSrc = ownImagePath ? resolveImageSrc(ownImagePath) : '';
  // Resolve the source-photo image from the ACTIVE observation (year-aware), so
  // As-found + a selected inspection shows that inspection's photo, not the latest.
  const sourcePhotoImagePath =
    !ownImagePath && activeObs?.sourcePhotoId
      ? data.indications.find((i) => i.id === activeObs.sourcePhotoId)?.properties.image || ''
      : '';
  const photoRelPath = ownImagePath || sourcePhotoImagePath;
  const photoImageSrc = photoRelPath ? resolveImageSrc(photoRelPath) : '';
  const photoNotes = existingIndication?.properties.notes || '';
  const hasPhoto = !!photoImageSrc;

  // Cross-inspection history: each observation's thumbnail (its source-photo
  // image), oldest→newest. Empty unless the fault was seen more than once — a
  // single-observation fault is fully represented by the main photo above.
  const observationHistory = useMemo(() => {
    if (!existingIndication) return [];
    const obs = deriveObservations(existingIndication);
    if (obs.length <= 1) return [];
    return obs.map((o) => {
      const relPath =
        o.image ||
        (o.sourcePhotoId ? data.indications.find((i) => i.id === o.sourcePhotoId)?.properties.image : '') ||
        '';
      return { obs: o, src: relPath ? resolveImageSrc(relPath) : '' };
    });
  }, [existingIndication, data.indications]);

  // ---- Image Upload ---- (the child's OWN image only; never the resolved source)
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedImagePath, setUploadedImagePath] = useState<string>(ownImagePath);
  const [uploadedPreviewUrl, setUploadedPreviewUrl] = useState<string>(ownImageSrc);

  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const relPath = uniqueRelPhotoPath(file.name);
    const blobUrl = storePhotoBlob(relPath, file);
    setUploadedImagePath(relPath);
    setUploadedPreviewUrl(blobUrl);
  }, []);

  const handleRemoveImage = useCallback(() => {
    setUploadedImagePath('');
    setUploadedPreviewUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // ---- Interactive mini-map (magnifying glass) ----
  // MBX-03B: created ONCE per popup mount; coordinate/geometry changes update
  // the existing map via flyTo/setData rather than recreating it. The prior
  // effect depended on `coordinates`, which the edit path passes as a fresh
  // array each render (centroidOfPolygon), so the billable mini-map was being
  // recreated on every re-render. View-only: pan/zoom + highlight, no mutation.
  const mapInstance = useMapStore((s) => s.map);
  const miniMapContainerRef = useRef<HTMLDivElement>(null);
  const miniMapRef = useRef<mapboxgl.Map | null>(null);
  const lastCenterRef = useRef<[number, number] | null>(null);
  const indicationGeometry = existingIndication?.geometry;

  useEffect(() => {
    if (!miniMapContainerRef.current || !mapInstance) return;

    const mainBearing = mapInstance.getBearing();

    const miniMap = new mapboxgl.Map({
      container: miniMapContainerRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: coordinates,
      zoom: 20,
      bearing: mainBearing,
      attributionControl: false,
      dragRotate: false,
    });
    miniMapRef.current = miniMap;
    lastCenterRef.current = [coordinates[0], coordinates[1]];

    miniMap.addControl(
      new mapboxgl.NavigationControl({ showCompass: false, visualizePitch: false }),
      'top-right'
    );

    // Follow the main map's rotation live (initial bearing is set above).
    // Optional calls: tests stub the main map without the event emitter.
    const syncBearing = () => miniMap.setBearing(mapInstance.getBearing());
    mapInstance.on?.('rotate', syncBearing);

    let cancelled = false;

    miniMap.on('style.load', async () => {
      const orthos = useProjectStore.getState().data.orthophotos;
      const overlays = useProjectStore.getState().data.overlays;

      // Mapbox image sources load via Image() with crossOrigin='anonymous',
      // which strips cookies — raw /api/v2/assets/ URLs return 401. Pre-sign
      // the asset URLs (tile-mode items skip this; tiles aren't auth-gated).
      const urlsNeedingResolution = [
        ...orthos
          .filter((o) => o.visible && o.url && !(o.tileStatus === 'ready' && !!o.tilesetId))
          .map((o) => o.url),
        ...overlays
          .filter((o) => o.visible && o.url && !(o.tileStatus === 'ready' && !!o.tilesetId))
          .map((o) => o.url),
      ];
      const signedMap = urlsNeedingResolution.length
        ? await resolveSignedUrls(urlsNeedingResolution)
        : new Map<string, string>();

      if (cancelled || miniMapRef.current !== miniMap) return;

      // Copy orthophoto layers from main map
      for (const ortho of orthos) {
        if (!ortho.visible || !ortho.url) continue;
        const srcId = `ortho-src-${ortho.id}`;
        const lyrId = `ortho-lyr-${ortho.id}`;
        if (miniMap.getSource(srcId)) continue;

        const useTiles = ortho.tileStatus === 'ready' && !!ortho.tilesetId;
        if (useTiles) {
          miniMap.addSource(srcId, {
            type: 'raster',
            tiles: [buildTileUrl(ortho.tilesetId!)],
            tileSize: 512,
            bounds: [ortho.bounds.minLng, ortho.bounds.minLat, ortho.bounds.maxLng, ortho.bounds.maxLat] as [number, number, number, number],
            minzoom: ortho.tileMinZoom ?? 10,
            maxzoom: ortho.tileMaxZoom ?? 22,
          });
        } else {
          miniMap.addSource(srcId, {
            type: 'image',
            url: signedMap.get(ortho.url) ?? ortho.url,
            coordinates: ortho.coordinates,
          });
        }
        miniMap.addLayer({
          id: lyrId,
          type: 'raster',
          source: srcId,
          paint: { 'raster-opacity': ortho.opacity / 100, 'raster-fade-duration': 0 },
        });
      }

      // Copy overlay layers
      for (const ov of overlays) {
        if (!ov.visible || !ov.url) continue;
        const srcId = `overlay-src-${ov.id}`;
        const lyrId = `overlay-lyr-${ov.id}`;
        if (miniMap.getSource(srcId)) continue;

        const useTiles = ov.tileStatus === 'ready' && !!ov.tilesetId;
        if (useTiles) {
          const lngs = ov.bounds.map((c) => c[0]);
          const lats = ov.bounds.map((c) => c[1]);
          miniMap.addSource(srcId, {
            type: 'raster',
            tiles: [buildTileUrl(ov.tilesetId!)],
            tileSize: 512,
            bounds: [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)] as [number, number, number, number],
            minzoom: ov.tileMinZoom ?? 10,
            maxzoom: ov.tileMaxZoom ?? 22,
          });
        } else {
          miniMap.addSource(srcId, {
            type: 'image',
            url: signedMap.get(ov.url) ?? ov.url,
            coordinates: ov.bounds,
          });
        }
        miniMap.addLayer({
          id: lyrId,
          type: 'raster',
          source: srcId,
          paint: { 'raster-opacity': ov.opacity, 'raster-fade-duration': 0 },
        });
      }

      // Draw the indication polygon highlight
      if (existingIndication?.geometry?.type === 'Polygon') {
        const typeColor = INDICATION_TYPES[existingIndication.type as keyof typeof INDICATION_TYPES]?.color || '#FF3B30';
        miniMap.addSource('indication-hl', {
          type: 'geojson',
          data: { type: 'Feature', geometry: existingIndication.geometry, properties: {} },
        });
        miniMap.addLayer({
          id: 'indication-hl-fill',
          type: 'fill',
          source: 'indication-hl',
          paint: { 'fill-color': typeColor, 'fill-opacity': 0.15 },
        });
        miniMap.addLayer({
          id: 'indication-hl-line',
          type: 'line',
          source: 'indication-hl',
          paint: { 'line-color': typeColor, 'line-width': 2 },
        });
      }
    });

    return () => {
      cancelled = true;
      mapInstance.off?.('rotate', syncBearing);
      miniMap.remove();
      miniMapRef.current = null;
      lastCenterRef.current = null;
    };
    // Create once per popup mount. `coordinates` / `existingIndication` are read
    // at creation only; later changes are applied by the camera + highlight
    // effects below, so they are intentionally excluded from these deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapInstance]);

  // Recenter the existing mini-map when coordinates change BY VALUE (the edit
  // path passes a new array reference each render — compare values, not refs, so
  // we neither recreate the map nor animate on every re-render).
  useEffect(() => {
    const miniMap = miniMapRef.current;
    if (!miniMap || !coordinates) return;
    const [lng, lat] = coordinates;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    const last = lastCenterRef.current;
    if (last && last[0] === lng && last[1] === lat) return;
    lastCenterRef.current = [lng, lat];
    miniMap.flyTo({ center: [lng, lat], zoom: 20, duration: 350 });
  }, [coordinates]);

  // Refresh the indication highlight via setData when geometry changes, reusing
  // the existing map/source instead of recreating it. The `indication-hl` source
  // exists only after style.load and only for polygon highlights (current
  // behavior) — skip safely otherwise.
  useEffect(() => {
    const miniMap = miniMapRef.current;
    if (!miniMap || !indicationGeometry || indicationGeometry.type !== 'Polygon') return;
    const src = miniMap.getSource('indication-hl') as mapboxgl.GeoJSONSource | undefined;
    if (!src) return;
    const feature: GeoJSON.Feature = { type: 'Feature', geometry: indicationGeometry, properties: {} };
    src.setData(feature);
  }, [indicationGeometry]);

  // ---- Analyze handler (toggles panel + starts analysis) ----
  const handleAnalyzeThermal = useCallback(async () => {
    setThermalActive(true);
    if (!annotator.isAnalyzed && photoRelPath) {
      // Try local blob first
      const entry = getPhotoBlobEntry(photoRelPath);
      if (entry?.file) {
        annotator.analyze(entry.file);
        return;
      }
      // Fallback: fetch from URL (server-loaded projects). credentials:'include'
      // ships the sl_refresh cookie so the asset GET authenticates the same way
      // the visible <img> tag does — bare fetch() drops cookies cross-origin.
      const src = resolveImageSrc(photoRelPath);
      if (src) {
        try {
          const resp = await fetch(src, { credentials: 'include' });
          if (!resp.ok) throw new Error(`Asset fetch failed: ${resp.status}`);
          const blob = await resp.blob();
          const fileName = photoRelPath.split('/').pop() || 'thermal.jpg';
          const file = new File([blob], fileName, { type: blob.type });
          annotator.analyze(file);
        } catch (err) {
          console.error('[Thermal] Failed to fetch image for analysis:', err);
          setThermalActive(false);
          useUIStore.getState().addNotification({
            type: 'error',
            title: 'Thermal analysis failed',
            message: 'Could not load image for thermal analysis.',
          });
        }
      }
    }
  }, [annotator, photoRelPath]);

  // ---- Save / Delete ----
  const handleSave = () => {
    onSave({
      ...formData,
      // Preserve the fault's current thermal when a HISTORICAL observation is on
      // screen — the annotator loaded that year's ROI only for display.
      thermalData: isViewingLatestObs ? thermalDataRef.current : existingIndication?.properties.thermalData,
      image: uploadedImagePath || undefined,
    });
  };

  const handleDelete = () => {
    if (onDelete && window.confirm('Delete this indication? This cannot be undone.')) {
      onDelete();
    }
  };

  const title = mode === 'add' ? 'Add Indication' : 'Edit Indication';

  return (
    <div className={`indication-popup${hasPhoto ? ' indication-popup--with-photo' : ''}`}>
      {/* ═══ Left panel: Photo + thermal canvas overlay ═══ */}
      {hasPhoto && (
        <div className="indication-popup__photo-panel">
          {/* Which inspection's photo is on screen — makes the As-found/Current +
              date-filter selection unambiguous (#5). */}
          {activeObs && (
            <div className="indication-popup__obs-badge" data-testid="obs-badge">
              {indicationViewMode === 'asFound' ? 'As found' : 'Current'}
              {activeObs.date ? ` · ${captureDay(activeObs.date)}` : ''}
            </div>
          )}
          <ThermalAnnotator
            imageSrc={photoImageSrc}
            rois={annotator.rois}
            points={annotator.points}
            activeRoiIndex={annotator.activeRoiIndex}
            onPointerDown={annotator.handlePointerDown}
            onPointerMove={annotator.handlePointerMove}
            onPointerUp={annotator.handlePointerUp}
            imgRef={imgRef}
            interactive={thermalActive && annotator.isAnalyzed}
          />
          {photoNotes && (
            <pre className="indication-popup__photo-notes">{photoNotes}</pre>
          )}
          {observationHistory.length > 0 && (
            <div className="indication-popup__history" data-testid="obs-history">
              <div className="indication-popup__history-label">
                Observation history · {observationHistory.length} inspections
              </div>
              <div className="indication-popup__history-strip">
                {observationHistory.map((h, idx) => {
                  const isActive = h.obs === activeObs;
                  const isLatest = idx === observationHistory.length - 1;
                  return (
                    <div key={idx} className={`obs-thumb${isActive ? ' obs-thumb--active' : ''}`}>
                      {h.src ? (
                        <img className="obs-thumb__img" src={h.src} alt={h.obs.date || 'observation'} loading="lazy" />
                      ) : (
                        <div className="obs-thumb__img obs-thumb__img--empty">no image</div>
                      )}
                      <div className="obs-thumb__date">
                        {h.obs.date || '—'}
                        {isActive ? ' · shown' : isLatest ? ' · latest' : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div ref={miniMapContainerRef} className="indication-popup__mini-map" />
        </div>
      )}

      {/* ═══ Right panel ═══ */}
      <div className="indication-popup__form-panel">
        {/* Header */}
        <div className="indication-popup__header">
          <h3>{thermalActive ? 'Thermal Analysis' : title}</h3>
          <div className="indication-popup__header-right">
            {hasPhoto && (
              <button
                type="button"
                className={`indication-popup__thermal-toggle${thermalActive ? ' indication-popup__thermal-toggle--active' : ''}`}
                onClick={thermalActive ? () => setThermalActive(false) : handleAnalyzeThermal}
              >
                {thermalActive ? '\u2190 Form' : 'Analyze Thermal'}
              </button>
            )}
            <span className="indication-popup__coords">
              {coordinates[0].toFixed(6)}, {coordinates[1].toFixed(6)}
            </span>
          </div>
        </div>

        {/* Content: Thermal panel OR Form */}
        {thermalActive ? (
          <div className="indication-popup__content">
            <ThermalControls
              mode={annotator.mode}
              onModeChange={annotator.setMode}
              onUndoRoi={annotator.undoRoi}
              onClearRois={annotator.clearRois}
              onClearPoints={annotator.clearPoints}
              isAnalyzing={annotator.isAnalyzing}
              isAnalyzed={annotator.isAnalyzed}
              statusMessage={annotator.statusMessage}
            />
            <ThermalStats
              rois={annotator.rois}
              points={annotator.points}
            />
            <ThermalAdvancedParams
              params={annotator.params}
              onChange={annotator.setParams}
              disabled={annotator.isAnalyzing}
            />
          </div>
        ) : (
          <div className="indication-popup__content">
            {/* Type */}
            <div className="form-row">
              <label htmlFor="ind-type">Type</label>
              <select
                id="ind-type"
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                className="form-select"
              >
                {Object.entries(INDICATION_TYPES).map(([type, config]) => (
                  <option key={type} value={type}>
                    {config.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom type name — only for the 'Custom' type */}
            {formData.type === 'Custom' && (
              <div className="form-row">
                <label htmlFor="ind-custom-type-name">Custom type name</label>
                <input
                  id="ind-custom-type-name"
                  type="text"
                  list="ind-custom-type-names"
                  placeholder="e.g. Fence damage"
                  value={formData.customTypeName || ''}
                  onChange={(e) => handleChange('customTypeName', e.target.value)}
                  className="form-input"
                />
                {existingCustomNames.length > 0 && (
                  <datalist id="ind-custom-type-names">
                    {existingCustomNames.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                )}
              </div>
            )}

            {/* Grid derivation banner — ADD: creation-time fill, derived on
                the RECT that will exist (same engine as quick-add — a bare
                point sees one cell of a String OC); EDIT: "grid says" chips
                vs stored, explicit Apply only. Hidden for share viewers. */}
            {!isReadOnly && (
            <TopologySuggestionBanner
              lngLat={coordinates}
              addGeometry={addGeometry}
              addType={formData.type}
              indicationId={mode === 'edit' ? indicationId : null}
              onAccept={(fields, customFieldsFromSuggestion) => {
                setFormData((prev) => {
                  const updated = {
                    ...prev,
                    // fields present in the derivation OVERWRITE the form —
                    // an edit-mode Apply is an explicit user acceptance
                    station: fields.station !== undefined ? fields.station : prev.station,
                    inverter: fields.inverter !== undefined ? fields.inverter : prev.inverter,
                    mppt: fields.mppt !== undefined ? fields.mppt : prev.mppt,
                    dccb: fields.dccb !== undefined ? fields.dccb : prev.dccb,
                    string: fields.string !== undefined ? fields.string : prev.string,
                    module: fields.module !== undefined ? fields.module : prev.module,
                  };
                  if (customFieldsFromSuggestion && Object.keys(customFieldsFromSuggestion).length > 0) {
                    updated.customFields = { ...(prev.customFields ?? {}), ...customFieldsFromSuggestion };
                  }
                  return updated;
                });
              }}
            />
            )}

            {/* Topology fields — only show non-empty + add more */}
            <TopologyFieldsGrid formData={formData} onChange={handleChange} />

            {/* Capture Date */}
            <div className="form-row form-row--grid">
              <div className="form-field">
                <label htmlFor="ind-date">Capture Date</label>
                <input
                  id="ind-date"
                  type="date"
                  value={formData.captureDate}
                  onChange={(e) => handleChange('captureDate', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            {/* Link Library & URL Override */}
            <div className="form-row form-row--grid">
              <div className="form-field">
                <label htmlFor="ind-link">Link library (optional)</label>
                <select
                  id="ind-link"
                  value={formData.linkLibrary}
                  onChange={(e) => handleChange('linkLibrary', e.target.value)}
                  className="form-select"
                >
                  <option value="">— none —</option>
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="ind-url">URL override (optional)</label>
                <input
                  id="ind-url"
                  type="url"
                  placeholder="https://..."
                  value={formData.urlOverride}
                  onChange={(e) => handleChange('urlOverride', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="form-row">
              <label htmlFor="ind-notes">Notes</label>
              <textarea
                id="ind-notes"
                rows={3}
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                className="form-textarea"
              />
            </div>

            {/* Image / Video Upload */}
            <div className="form-row">
              <label>{formData.type === 'Video' ? 'Video' : 'Image'}</label>
              <div className="form-image-upload">
                {uploadedPreviewUrl ? (
                  <div className="form-image-upload__preview">
                    {formData.type === 'Video' ? (
                      <video src={uploadedPreviewUrl} controls preload="metadata" style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 'var(--r-sm)' }} />
                    ) : (
                      <img src={uploadedPreviewUrl} alt="Uploaded" />
                    )}
                    <button
                      type="button"
                      className="form-image-upload__remove"
                      onClick={handleRemoveImage}
                      title="Remove"
                    >
                      &times;
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="form-image-upload__btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Click to upload {formData.type === 'Video' ? 'video' : 'image'}
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={formData.type === 'Video' ? 'video/*,image/*' : 'image/*'}
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            {/* Create Issue checkbox */}
            <div className="form-row">
              <label className="form-checkbox">
                <input
                  type="checkbox"
                  checked={formData.createIssue}
                  onChange={(e) => handleChange('createIssue', e.target.checked)}
                />
                <span>Create Issue for Tasks</span>
              </label>
              <span className="form-hint">
                Click to add this indication to the Tasks & Scheduling tab
              </span>
            </div>

            {/* Resolution status (Phase 1) — edit mode only. Interactive for
                editors; read-only badge for share viewers. Writes go through
                the server-stamped setIndicationStatus action. */}
            {existingIndication && (
              <div className="form-row">
                <label>Resolution status</label>
                {isReadOnly ? (
                  <div className="indication-status-badge" data-status={currentStatus}>
                    {STATUS_LABELS[currentStatus]}
                  </div>
                ) : (
                  <div className="indication-status-toggle" role="group" aria-label="Resolution status">
                    {STATUS_ORDER.map((s) => (
                      <button
                        key={s}
                        type="button"
                        data-status={s}
                        className={`indication-status-toggle__btn${currentStatus === s ? ' is-active' : ''}`}
                        onClick={() => setIndicationStatus(existingIndication.id, s)}
                      >
                        {STATUS_LABELS[s]}
                      </button>
                    ))}
                  </div>
                )}
                {latestStatusEvent && (
                  <span className="form-hint">
                    {STATUS_LABELS[latestStatusEvent.status]}
                    {latestStatusEvent.at ? ` · ${new Date(latestStatusEvent.at).toLocaleString()}` : ''}
                    {latestStatusEvent.by ? ` · ${latestStatusEvent.by}` : ''}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        {/* F-02E-2B: share viewers see only Close — Save and Delete are
            mutations. EditorPage already gates the open-modal pathway,
            so this branch is defense-in-depth in case the modal opens
            via some future code path. */}
        <div className="indication-popup__footer" data-testid="indication-popup-footer">
          {!isReadOnly && (
            <Button variant="primary" onClick={handleSave} data-testid="indication-popup-save">
              Save
            </Button>
          )}
          {!isReadOnly && mode === 'edit' && onDelete && (
            <Button variant="danger" onClick={handleDelete} data-testid="indication-popup-delete">
              Delete
            </Button>
          )}
          {/* Field navigation — pure client-side, so share viewers get it too. */}
          {existingIndication && (
            <Button
              variant="secondary"
              onClick={() => {
                const c = indCenter(existingIndication.geometry as GeoJSON.Geometry) ?? coordinates;
                const label =
                  existingIndication.properties.title ||
                  (existingIndication.type === 'Custom' && existingIndication.properties.customTypeName) ||
                  INDICATION_TYPES[existingIndication.type]?.label ||
                  existingIndication.type;
                useMapStore.getState().setGuideTarget({ lng: c[0], lat: c[1], label });
                onCancel();
              }}
              data-testid="indication-popup-guide"
            >
              Guide me
            </Button>
          )}
          <Button variant="ghost" onClick={onCancel}>
            {isReadOnly ? 'Close' : 'Cancel'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Topology fields: show only non-empty, with "+ Add" for hidden ones ──

interface FieldDef {
  key: string;
  label: string;
  id: string;
  isCustom?: boolean;
}

const STANDARD_TOPOLOGY_FIELDS: FieldDef[] = [
  { key: 'station', label: 'Station', id: 'ind-station' },
  { key: 'mppt', label: 'MPPT', id: 'ind-mppt' },
  { key: 'inverter', label: 'Inverter', id: 'ind-inverter' },
  { key: 'dccb', label: 'DCCB', id: 'ind-dccb' },
  { key: 'string', label: 'String', id: 'ind-string' },
  { key: 'module', label: 'Module', id: 'ind-module' },
];

function TopologyFieldsGrid({
  formData,
  onChange,
}: {
  formData: IndicationFormData;
  onChange: (field: keyof IndicationFormData, value: string | boolean) => void;
}) {
  const [showAll, setShowAll] = useState(false);

  // Build custom field defs from formData.customFields
  const customFieldDefs = useMemo((): FieldDef[] => {
    if (!formData.customFields) return [];
    return Object.keys(formData.customFields).map((key) => ({
      key,
      label: key,
      id: `ind-custom-${key}`,
      isCustom: true,
    }));
  }, [formData.customFields]);

  const allFields = useMemo(() => [...STANDARD_TOPOLOGY_FIELDS, ...customFieldDefs], [customFieldDefs]);

  const visibleFields = useMemo(() => {
    if (showAll) return allFields;
    const active = allFields.filter((f) =>
      f.isCustom
        ? !!(formData.customFields?.[f.key])
        : !!(formData as any)[f.key]
    );
    return active.length > 0 ? active : allFields;
  }, [formData, showAll, allFields]);

  const hiddenCount = allFields.length - visibleFields.length;

  // Group into pairs for 2-column grid
  const pairs: FieldDef[][] = [];
  for (let i = 0; i < visibleFields.length; i += 2) {
    pairs.push(visibleFields.slice(i, i + 2));
  }

  const handleCustomFieldChange = useCallback((key: string, value: string) => {
    const updated = { ...(formData.customFields ?? {}), [key]: value };
    onChange('customFields' as keyof IndicationFormData, updated as any);
  }, [formData.customFields, onChange]);

  return (
    <>
      {pairs.map((pair, i) => (
        <div key={i} className="form-row form-row--grid">
          {pair.map((f) => (
            <div key={f.key} className="form-field">
              <label htmlFor={f.id}>{f.label}</label>
              <input
                id={f.id}
                type="text"
                value={f.isCustom ? (formData.customFields?.[f.key] || '') : ((formData as any)[f.key] || '')}
                onChange={(e) => f.isCustom
                  ? handleCustomFieldChange(f.key, e.target.value)
                  : onChange(f.key as keyof IndicationFormData, e.target.value)
                }
                className="form-input"
              />
            </div>
          ))}
        </div>
      ))}
      {hiddenCount > 0 && !showAll && (
        <button
          type="button"
          className="form-add-field-btn"
          onClick={() => setShowAll(true)}
        >
          + {hiddenCount} more field{hiddenCount > 1 ? 's' : ''}
        </button>
      )}
    </>
  );
}
